import {
  Box,
  Button,
  Link,
  Stack,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Toolbar,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  LinearProgress,
  SelectChangeEvent,
  useTheme,
} from '@mui/material';
import { useCallback, useState, useMemo } from 'react';
import { Redirect } from 'react-router-dom';

import DashboardHeader from '@/@mui/dashboard/DashboardHeader';
import { useGetAllBuildings } from '@/api/endpoints/building/useGetAllBuildings';
import { BuildingModelWithLocationAndCounts } from '@/api/models/building';
import { ChevronLeftIcon } from '@/assets/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/assets/icons/ChevronRightIcon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { SearchMdIcon } from '@/assets/icons/SearchMdIcon';
import { PMPRoutes } from '@/config/routes';
import { usePropertyManager } from '@/context/PropertyManagerProvider';
import { getResolvedRoutePath } from '@/utils/router';

import BuildingCreateModal from '@/components/building/BuildingCreateModal';

type SortableField = 'name' | 'address_text' | 'location';

type VirtualSortableField = '_count.units' | 'tenants' | 'issues';

const TABLE_HEAD: {
  id: SortableField | VirtualSortableField;
  label: string;
  sortable: boolean;
  width: string;
}[] = [
  { id: 'name', label: 'Name', sortable: true, width: '20%' },
  { id: 'address_text', label: 'Address', sortable: true, width: '40%' },
  { id: 'location', label: 'Location', sortable: true, width: '15%' },
  { id: '_count.units', label: 'Units', sortable: true, width: '8%' },
  { id: 'tenants', label: 'Tenants', sortable: true, width: '8%' },
  { id: 'issues', label: 'Issues', sortable: false, width: '9%' },
];

type Order = 'asc' | 'desc';

const BuildingListPage: React.FC = () => {
  const { isSingleFamilyAccount } = usePropertyManager();
  const theme = useTheme();
  const [isBuildingCreateModalOpen, setIsBuildingCreateModalOpen] = useState(false);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<SortableField | VirtualSortableField>('name');
  const [filterName, setFilterName] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const getAllBuildingsQuery = useGetAllBuildings();

  const handleRequestSort = (property: SortableField | VirtualSortableField) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>, _: React.ReactNode) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Filter and sort the data
  const filteredData = useMemo(() => {
    let filtered = getAllBuildingsQuery.data || [];

    // Apply name filter
    if (filterName) {
      filtered = filtered.filter(
        item =>
          item.name?.toLowerCase().includes(filterName.toLowerCase()) ||
          item.address_text.toLowerCase().includes(filterName.toLowerCase())
      );
    }

    // Sort the data
    return [...filtered].sort((a, b) => {
      if (orderBy === '_count.units') {
        return order === 'asc'
          ? (a._count?.units ?? 0) - (b._count?.units ?? 0)
          : (b._count?.units ?? 0) - (a._count?.units ?? 0);
      }

      if (orderBy === 'tenants') {
        const aTenants = a.units.reduce((sum, unit) => sum + unit._count.tenants, 0);
        const bTenants = b.units.reduce((sum, unit) => sum + unit._count.tenants, 0);
        return order === 'asc' ? aTenants - bTenants : bTenants - aTenants;
      }

      if (orderBy === 'location') {
        return order === 'asc'
          ? (a.location?.name ?? '').localeCompare(b.location?.name ?? '')
          : (b.location?.name ?? '').localeCompare(a.location?.name ?? '');
      }

      // Handle direct properties
      const aValue = a[orderBy as keyof BuildingModelWithLocationAndCounts];
      const bValue = b[orderBy as keyof BuildingModelWithLocationAndCounts];
      return order === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [getAllBuildingsQuery.data, filterName, order, orderBy]);

  // Get the current page of data
  const displayedData = useMemo(
    () => filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, page, rowsPerPage]
  );

  const onAddBuildingClick = useCallback(() => {
    setIsBuildingCreateModalOpen(true);
  }, []);

  if (isSingleFamilyAccount) {
    return <Redirect to={PMPRoutes.propertyLocationList} />;
  }

  return (
    <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%' }}>
      <DashboardHeader title="All Buildings">
        <Button
          color="primary"
          variant="contained"
          size="medium"
          startIcon={<PlusIcon />}
          onClick={onAddBuildingClick}
        >
          Add Building
        </Button>
      </DashboardHeader>

      <Typography variant="body1" sx={{ mb: 2 }}>
        This is where you can see the buildings you are managing. You can click on an existing
        building or add a new building to manage tenants and knowledge base.
      </Typography>

      <Card sx={{ bgcolor: 'common.white' }}>
        <Toolbar
          sx={{
            height: 64,
            display: 'flex',
            justifyContent: 'space-between',
            p: theme.spacing(2),
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          <OutlinedInput
            fullWidth
            value={filterName}
            onChange={handleFilterName}
            placeholder="Search buildings..."
            startAdornment={
              <InputAdornment position="start">
                <SearchMdIcon sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            }
            sx={{
              maxWidth: 320,
              bgcolor: 'common.white',
            }}
          />
        </Toolbar>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {TABLE_HEAD.map(headCell => (
                  <TableCell
                    key={headCell.id}
                    align="left"
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={{
                      width: headCell.width,
                      borderRight: 'none',
                      '&:last-child': {
                        borderRight: 'none',
                      },
                      bgcolor: theme.palette.secondary[200],
                      textTransform: 'none',
                      color: 'text.primary',
                      '& .MuiTableSortLabel-root': {
                        textTransform: 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: { xs: '0.875rem', sm: 'inherit' },
                        fontWeight: 'bold',
                        color: 'text.primary',
                        '&:hover': {
                          color: 'text.primary',
                        },
                        '&.Mui-active': {
                          color: 'text.primary',
                          '&:hover': {
                            color: 'text.primary',
                          },
                        },
                      },
                    }}
                  >
                    {headCell.sortable ? (
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={() => handleRequestSort(headCell.id)}
                      >
                        {headCell.label}
                      </TableSortLabel>
                    ) : (
                      <Typography
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: { xs: '0.875rem', sm: 'inherit' },
                          fontWeight: 'bold',
                          color: 'text.primary',
                          '&:hover': {
                            color: 'text.primary',
                          },
                        }}
                      >
                        {headCell.label}
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {getAllBuildingsQuery.isLoading && (
                <TableRow>
                  <TableCell colSpan={TABLE_HEAD.length} align="center" sx={{ py: 3 }}>
                    <LinearProgress sx={{ width: '100%' }} />
                  </TableCell>
                </TableRow>
              )}
              {!getAllBuildingsQuery.isLoading && displayedData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={TABLE_HEAD.length} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No buildings found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!getAllBuildingsQuery.isLoading &&
                displayedData.length > 0 &&
                displayedData.map(row => (
                  <TableRow
                    key={String(row.id)}
                    sx={{
                      bgcolor: theme.palette.secondary.contrastText,
                      '&:hover': {
                        backgroundColor: `${theme.palette.secondary[200]} !important`,
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease',
                      },
                    }}
                  >
                    <TableCell sx={{ width: '20%' }}>
                      <Link
                        href={getResolvedRoutePath(PMPRoutes.buildingView, { id: String(row.id) })}
                        sx={{
                          color: 'text.primary',
                          textDecoration: 'none',
                          '&:hover': {
                            color: theme.palette.primary.main,
                          },
                        }}
                      >
                        {row.name ?? 'Unnamed'}
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '40%',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 0, // Important to trigger ellipsis within flex container
                      }}
                    >
                      {row.address_text || 'N/A'}
                    </TableCell>

                    <TableCell sx={{ width: '15%' }}>
                      {row.location ? (
                        <Link
                          href={getResolvedRoutePath(PMPRoutes.propertyLocationView, {
                            id: String(row.location.id),
                          })}
                          sx={{
                            color: 'text.primary',
                            textDecoration: 'none',
                            '&:hover': {
                              color: theme.palette.primary.main,
                            },
                          }}
                        >
                          {row.location.name}
                        </Link>
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    <TableCell sx={{ width: '8%' }}>{row._count?.units ?? 0}</TableCell>
                    <TableCell sx={{ width: '8%' }}>
                      {row.units.reduce((sum, unit) => sum + unit._count.tenants, 0) ?? 0}
                    </TableCell>
                    <TableCell sx={{ width: '9%' }}>-</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Rows per page:
            </Typography>
            <Select
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              size="small"
              sx={{ width: 80 }}
              inputProps={{ 'aria-label': 'rows per page' }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {`${page * rowsPerPage + 1}-${Math.min(
                (page + 1) * rowsPerPage,
                filteredData.length
              )} of ${filteredData.length}`}
            </Typography>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 0}
              size="small"
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage) - 1}
              size="small"
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        </Box>
      </Card>

      {isBuildingCreateModalOpen && (
        <BuildingCreateModal onClose={() => setIsBuildingCreateModalOpen(false)} />
      )}
    </Stack>
  );
};

export default BuildingListPage;
